import { Dialog } from '@eventbrite/eds-dialog';
import { withTooltip } from '@eventbrite/eds-tooltip';
import { gettext } from '@eventbrite/i18n';
import { css, cx } from 'emotion';
import React, { useState } from 'react';
import { useIsMobileDevice } from '../hooks/useIsMobileDevice';
import { useIsTouchDevice } from '../hooks/useIsTouchDevice';
import { useUrgencyCategories } from './provider';
import { UrgencyCategoryType } from './types';

interface BaseUrgencyCategoryProps {
    category: string;
    label: string;
    description: string;
}

export const urgencyCategoriesContent: {
    [key: string]: {
        label: string;
        description: string;
    };
} = {
    [UrgencyCategoryType.EarlyBird]: {
        label: gettext('Early access').toString(),
        description: gettext(
            'Tickets may be available at a lower price for a limited time.',
        ).toString(),
    },
    [UrgencyCategoryType.Popular]: {
        label: gettext('Popular').toString(),
        description: gettext(
            "50% of this event's ticket are already sold! Get yours before they sell out.",
        ).toString(),
    },
    [UrgencyCategoryType.New]: {
        label: gettext('Just Added!').toString(),
        description: gettext(
            'This event was added to Eventbrite in the last 3 days. Be the first to get tickets.',
        ).toString(),
    },
};

export const UrgencyCategories: React.FC = () => {
    const categories = useUrgencyCategories();
    const isMobileDevice = useIsMobileDevice();
    const isTouchDevice = useIsTouchDevice();

    const urgencyCategoriesToShow = Object.keys(categories).filter(
        (categoryType) => categoryType,
    );

    return (
        <>
            {urgencyCategoriesToShow.map(
                (urgencyCategory: string, index: number) => {
                    const { label, description } =
                        urgencyCategoriesContent[
                            urgencyCategory as keyof typeof urgencyCategoriesContent
                        ];

                    const Component =
                        isMobileDevice || isTouchDevice
                            ? UrgencyCategoryWithDialog
                            : UrgencyCategoryWithTooltip;

                    return (
                        <Component
                            key={index}
                            category={urgencyCategory}
                            label={label}
                            description={description}
                        />
                    );
                },
            )}
        </>
    );
};

const UrgencyCategoryTag: React.FunctionComponent<{
    category: string;
    label: string;
}> = ({ category, label }) => {
    return (
        <span
            className={cx(styles.base, styles[category as keyof typeof styles])}
        >
            {label}
        </span>
    );
};

const UrgencyCategoryWithTooltip: React.FunctionComponent<
    BaseUrgencyCategoryProps
> = ({ category, label, description }) => {
    const BaseTooltipComponent = withTooltip(UrgencyCategoryTag);

    return (
        <BaseTooltipComponent
            category={category}
            label={label}
            tooltipId={label}
            tooltipText={
                <div className={cx(styles.tooltip)}>{description}</div>
            }
            tooltipStyle={'phab'}
            tooltipArrowLocation={'left'}
        />
    );
};

const UrgencyCategoryWithDialog: React.FunctionComponent<
    BaseUrgencyCategoryProps
> = ({ category, label, description }) => {
    const [isModalOpen, setIsOpen] = useState(false);

    const dialogContent = (
        <>
            <UrgencyCategoryTag category={category} label={label} />
            <span className={cx(styles.dialogDescription)}>{description}</span>
        </>
    );

    return (
        <div className={cx(styles.modal)}>
            <div
                onClick={() => setIsOpen(true)}
                className={cx(
                    styles.base,
                    styles[category as keyof typeof styles],
                )}
            >
                {label}
            </div>

            <Dialog
                isShown={isModalOpen}
                isClosable={true}
                onClose={() => setIsOpen(false)}
                message={dialogContent}
            />
        </div>
    );
};

const styles = {
    base: css`
        border-radius: 8px;
        column-gap: 8px;
        color: #1e0a3c;
        display: inline-flex;
        flex-shrink: 0;
        font-weight: 600;
        line-height: 1;
        margin: 4px;
        padding: 7px 12px;
    `,
    [UrgencyCategoryType.EarlyBird]: css`
        background-color: #ffe6be;
    `,
    [UrgencyCategoryType.Popular]: css`
        background-color: #faafa0;
    `,
    [UrgencyCategoryType.New]: css`
        background-color: #cddfd6;
    `,
    tooltip: css`
        max-width: 200px;
    `,
    modal: css`
        .eds-modal {
            align-items: flex-start;
        }
        .eds-modal__body {
            width: 100%;
            height: auto;
            margin-top: 76px;
            margin-left: 16px;
            margin-right: 16px;
            border-radius: 16px;
        }

        .eds-dialog__message {
            margin: 0;
            text-align: left;
        }
        .eds-modal__content__children {
            padding: 24px !important;
        }

        .eds-btn--button {
            background-color: #fff;
        }

        .eds-btn--button:focus-visible {
            background-color: #fff;
            outline-width: 0;
        }
    `,
    dialogDescription: css`
        display: flex;
        margin: 4px;
        font-size: 16px;
        color: #1e0a3c;
        font-weight: normal;
    `,
};
